<template>
  <div class="px-3">
    <PageHeader showBreadcrumbs page-title="Hours and payments report">
      <template #buttons>
        <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          class="switch"
          size="sm"
          switch
          v-model="includeLoggedLastMonth"
        >Include logged hours from last month</b-form-checkbox>
      </div>
      <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          class="switch"
          size="sm"
          switch
          v-model="includePaidLastMonth"
        >Include paid hours from last month</b-form-checkbox>
      </div>
      <div class="d-flex align-items-center table-text">
        <b-form-checkbox
          class="switch"
          size="sm"
          switch
          v-model="showOnlyDebt"
        >Show only projects with debt</b-form-checkbox>
      </div>
      </template>
    </PageHeader>

    <div v-if="isDataLoading" class="content-loading-wrapper" style="min-height: 60vh;">
      <Preloader class="centered-preloader large-spinner" />
    </div>
    <div v-else class="table-container">
      <div class="d-flex justify-content-between align-items-center bg-white">
        <div class="d-flex align-items-center mb-2">
          <ProjectsStateListComponent
            :projects-list="getProjectsForState()"
            :table-name="uiTableName"
            @changeActive="handleHideProject($event)"
            @initialized="handleProjectsFilterInitialized($event)"
            class="mr-4"
          />
        </div>
      </div>

      <div ref="tableContain">
        <b-table-simple
          :fixed="false"
          class="table__theme-default slideout__table position-relative"
          hover
          id="my-table"
          responsive
          stripped
          v-if="reportData.length && projectsFilterLoaded"
        >
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>

          <b-thead head-variant="light">
            <b-tr :class="{'sticky-head': getCoordinates > 0}" :style="{'top': getCoordinates + 'px'}" class="thead-row row-top">
              <b-th :style="{'min-width': thsWidth[`th_project`] + 'px'}" class="text-uppercase">Project and client</b-th>
              <b-th :style="{'min-width': thsWidth[`th_logged`] + 'px'}" class="text-uppercase text-right">Logged hours</b-th>
              <b-th :style="{'min-width': thsWidth[`th_paid`] + 'px'}" class="text-uppercase text-right">Paid hours</b-th>
              <b-th :style="{'min-width': thsWidth[`th_summary`] + 'px'}" class="text-uppercase text-right">Summary</b-th>
              <b-th :style="{'min-width': thsWidth[`th_summary`] + 'px'}" class="text-uppercase text-left">Comment</b-th>
            </b-tr>
            <b-tr>
              <b-th class="text-uppercase" sticky-column><div :ref="`th_project`">Project and client</div></b-th>
              <b-th class="text-uppercase text-right" sticky-column><div :ref="`th_logged`">Logged hours</div></b-th>
              <b-th class="text-uppercase text-right" sticky-column><div :ref="`th_paid`">Paid hours</div></b-th>
              <b-th class="text-uppercase text-right" sticky-column><div :ref="`th_summary`">Summary</div></b-th>
              <b-th class="text-uppercase text-left" sticky-column v-if="$can(permissions.ReadAllComments)"><div :ref="`th_summary`">Comment</div></b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <!-- If you're ever going to refactor this code please try to get rid of the eslint-disable-next-line vue/no-use-v-if-with-v-for in the next line -->
            <!-- eslint-disable-next-line vue/no-use-v-if-with-v-for -->
            <b-tr :key="i" class="table-row" v-for="(overviewItem, i) in reportData" v-if="projectState[overviewItem.project.id]">
              <b-td sticky-column>
                <div class="d-flex text-left align-items-center">
                  <ProjectInitialsButton
                    :project-name="overviewItem.project.name"
                    :project-color="overviewItem.project.color"
                    :project-data="overviewItem.project"
                    @click="getSlideOut"
                  />
                  <TableProjectItem
                    :link="overviewItem.project.key"
                    hideNumber
                    :project-clients="overviewItem.project.clientNames"
                    :project-name="overviewItem.project.name"
                    :project-subtitle="overviewItem.project.subtitle"
                    view="table-row"
                  />
                </div>
              </b-td>
              <b-td class="text-right">
                <div>{{ overviewItem.loggedHours | round(2) }}</div>
              </b-td>
              <b-td class="text-right">
                <div>{{ overviewItem.paidHours | round(2) }}</div>
              </b-td>
              <b-td class="text-right">
                <span :class="overviewItem.summary < 0 ? 'summary-danger' : 'summary-success'">{{ overviewItem.summary | round(2) }}</span>
              </b-td>
              <b-td v-if="$can(permissions.ReadAllComments)">
                <div class="user-comment__list p-0">
                  <TextEditor
                    :entity-id="overviewItem.project.id"
                    :entity-type="commentEntityTypes.ReportsHoursAndPayments"
                    :show-text="true"
                    data-already-loaded
                  />
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
      <div v-if="reportData.length === 0">
      </div>
    </div>

    <SlideOutContainerViaPortal className="slide-out-under-popup">
      <div v-if="showSlideOut">
        <TableWithDetails ref="tableWithDetails" :current-project="openProject" :breadcrumbs="slideoutBreadcrumbs" :invoiceFormBreadcrumbs="nestedInvoiceFormBreadcrumbs" :customOnClose="handleNestedInvoiceFormClose" :customSaveInvoice="handleNestedInvoiceSave" :showCloseButton="true" :closeButtonHandler="closeSlideOut" />
      </div>
    </SlideOutContainerViaPortal>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import { permissions } from '@mocks/permissions'
import { commentEntityTypes } from '@mocks/comment-entity-types'
import ProjectInitialsButton from '@components/ProjectInitialsButton'
import StickyHeaderMixin from '@/mixins/StickyHeaderMixin'
import ScrollPositionMixin from '@/mixins/ScrollPositionMixin'
import ProjectsStateListComponent from '@components/ProjectsStateListComponent'
import TableProjectItem from '@components/tables/TableProjectItem'
import TableWithDetails from '@components/tables/TableWithDetails'
import TextEditor from '@components/TextEditor'
import SlideOutContainerViaPortal from '@components/transition/SlideOutContainerViaPortal.vue'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader.vue'

export default {
  name: 'TableHPReportOverview',
  mixins: [ StickyHeaderMixin, ScrollPositionMixin ],
  components: {
    ProjectsStateListComponent,
    TableProjectItem,
    TableWithDetails,
    TextEditor,
    SlideOutContainerViaPortal,
    PageHeader,
    Preloader,
    ProjectInitialsButton
  },
  data () {
    return {
      permissions,
      uiTableName: 'hours_and_payments_report_overview',
      isDataLoading: true,
      includeLoggedLastMonth: false,
      includePaidLastMonth: false,
      showOnlyDebt: true,
      reportData: [],
      showSlideOut: false,
      openProject: null,
      showEditor: false,
      projectState: {},
      allActiveProjects: 0,
      projectsFilterLoaded: false,
      commentEditorOptions: {
        entity_type: commentEntityTypes.ReportsHoursAndPayments,
        data_already_loaded: true
      }
    }
  },
  async mounted () {
    if (this.$can(permissions.ReadAllComments)) {
      await this.fetchComments({ type: commentEntityTypes.ReportsHoursAndPayments })
    }
    await this.fetchReportsHoursAndPaymentsOverviewReport()
  },
  computed: {
    commentEntityTypes () {
      return commentEntityTypes
    },
    ...mapGetters({
      getTableHiddenColumnKeys: 'frontView/getTableHiddenColumnKeys'
    }),
    slideoutBreadcrumbs () {
      return [
        { text: 'Home', to: { name: 'home' } },
        { text: 'Hours and payments report',
          click: this.handleSlideoutBreadcrumbClick
        }
      ]
    },
    nestedInvoiceFormBreadcrumbs () {
      const projectName = this.openProject?.name || this.openProject?.key || ''
      return [
        { text: 'Home', to: { name: 'home' } },
        { text: 'Hours and payments report', click: this.handleNestedBreadcrumbToHoursReport },
        { text: projectName, click: this.handleNestedBreadcrumbToProjectDetails }
      ]
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments',
      addHiddenColumnKey: 'frontView/addHiddenColumnKey',
      removeHiddenColumnKey: 'frontView/removeHiddenColumnKey'
    }),
    handleSlideoutBreadcrumbClick () {
      this.closeSlideOut()
      // Always navigate to clean up the URL
      this.$router.push({ name: 'finances-reports-hours-and-payments-overview' }).catch(() => {})
      // Restore scroll position after slideout closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    handleNestedBreadcrumbToHoursReport () {
      // Close invoice form and slideout, go back to hours and payments overview
      if (this.$refs.tableWithDetails && this.$refs.tableWithDetails.invoiceFormData) {
        this.$refs.tableWithDetails.invoiceFormData.show = false
      }
      this.closeSlideOut()
      // Navigate to overview page
      this.$router.push({ name: 'finances-reports-hours-and-payments-overview' }).catch(() => {})
      // Restore scroll position after slideout closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    handleNestedBreadcrumbToProjectDetails () {
      // Close invoice form in TableWithDetails but keep hours and payments slideout open
      if (this.$refs.tableWithDetails && this.$refs.tableWithDetails.invoiceFormData) {
        this.$refs.tableWithDetails.invoiceFormData.show = false
      }
    },
    handleNestedInvoiceFormClose () {
      // Custom handler for when nested invoice form is closed
      if (this.openProject?.id) {
        const newUrl = `/finances-reports-hours-and-payments/${this.openProject.id}`
        window.history.pushState({}, '', newUrl)
      }
    },
    handleNestedInvoiceSave (invoiceData) {
      // Custom handler for when nested invoice is saved
      // This should match the behavior from FinanceSalesReportPage
      return this.$api.invoices.createInvoice(invoiceData)
        .then(response => {
          if (response.status === 200) {
            this.$toast.success('Invoice created successfully')
            // Refresh the project data if needed
            if (this.$refs.tableWithDetails) {
              this.$refs.tableWithDetails.fetchReportsHoursAndPayments()
            }
          }
          return response
        })
        .catch(error => {
          this.$toast.error('Failed to create invoice')
          throw error
        })
    },
    getSum (arr, key) {
      let sum = 0
      for (let i in arr) {
        sum += arr[i][key]
      }
      return sum
    },
    getAmount (data) {
      return data.amount + data.amount_not_paid
    },
    getSlideOut (project) {
      this.scrollToTheTop()
      this.showSlideOut = true
      this.openProject = {
        id: project.id,
        name: project.name,
        key: project.key
      }
      // Update URL to include project ID without navigation
      const newUrl = `/finances-reports-hours-and-payments/${project.id}`
      window.history.pushState({}, '', newUrl)
      window.scrollTo(0, 0)
    },
    closeSlideOut () {
      this.showSlideOut = false
      this.openProject = null
      window.history.pushState({}, '', '/finances-reports-hours-and-payments-overview')
      // Restore scroll position after slideout closes with a small delay
      setTimeout(() => {
        this.restoreScrollPosition()
      }, 300)
    },
    fetchReportsHoursAndPaymentsOverviewReport () {
      return new Promise((resolve, reject) => {
        this.isDataLoading = true
        const requestParams = {
          includeLastMonthLogs: this.includeLoggedLastMonth,
          includeLastMonthPaid: this.includePaidLastMonth,
          showOnlyDebt: this.showOnlyDebt
        }
        return this.$api.finances
          .getReportsHoursAndPaymentsOverview(requestParams)
          .then(response => {
            if (response.status === 200) {
              this.reportData = response.data
            }
            resolve(true)
          })
          .catch(error => {
            this.errorNotifications(error)
            reject(error)
          })
          .finally(() => {
            this.isDataLoading = false
          })
      })
    },
    getProjectsForState () {
      const reportItems = cloneDeep(this.reportData)
      if (!reportItems || !Array.isArray(reportItems)) {
        return []
      }
      return reportItems.map(item => item.project)
    },
    handleProjectsFilterInitialized (hiddenProjectIds) {
      const projects = this.getProjectsForState()
      if (!projects || projects.length === 0) {
        this.projectsFilterLoaded = true
        return
      }

      for (const project of projects) {
        this.projectState[project.id] = !hiddenProjectIds.includes(project.id)
      }

      this.allActiveProjects = Object.values(this.projectState).filter(item => !!item).length
      this.projectsFilterLoaded = true
    },
    handleHideProject (disProjects) {
      const projects = this.getProjectsForState()
      for (const i in projects) {
        if (disProjects.some(id => projects[i].id === id)) {
          this.projectState[projects[i].id] = false
        } else {
          this.projectState[projects[i].id] = true
        }
      }
      this.allActiveProjects = Object.values(this.projectState).filter(item => !!item).length
      this.$forceUpdate()
    }
  },
  watch: {
    'includeLoggedLastMonth': function () {
      this.projectsFilterLoaded = false
      this.fetchReportsHoursAndPaymentsOverviewReport()
    },
    'includePaidLastMonth': function () {
      this.projectsFilterLoaded = false
      this.fetchReportsHoursAndPaymentsOverviewReport()
    },
    'showOnlyDebt': function () {
      this.projectsFilterLoaded = false
      this.fetchReportsHoursAndPaymentsOverviewReport()
    }
  }
}
</script>

<style lang="scss" scoped>
.table-container {
  padding: 14px 16px 0;
  border-radius: 10px 10px 0 0;
  background-color: white;

  .table-row {
    height: 30px;
    td {
      padding: 6px 4px;
    }
  }

  .table-text {
    font-size: 12px;
    font-weight: 500;
    color: #7489A9;
  }
}
.table .thead-light th {
  color: #7489A9;
  line-height: 20px;
}
.slideout__table {
  &.table__theme-default {
    .table {
      background-color: #fff;
      tr {
        th {
          text-transform: none;
          background-color: #fff;
        }
      }
    }
  }
  .group-border {
    border-left: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
  }
  .tfoot {
    font-weight: bold;
    border-top: 1px solid #E4E7EE;
  }
}
.member-name {
  max-width: 200px;
  min-width: 200px;

  ::v-deep.user-name {
    max-width: 140px;
  }
  ::v-deep.user-position {
    max-width: 120px;
  }
}
.month-group {
  min-width: 70px;
}
.commission {
  font-size: 16px;
  cursor: pointer;
  color: #3D5170;
  .text-on {
    color: #3FA52F;
  }
  .text-off {
    color: #DC3545;
  }
}

 ::v-deep .project-subtitle, ::v-deep .project-title {
    margin-bottom: 0;
    max-width: max-content;
  }

  ::v-deep .project-title {
    line-height: 20px;
  }

  ::v-deep  .wrap.project-item {
    flex: 1 1 auto;          /* allow it to shrink */
    min-width: 0;            /* CRUCIAL in flexbox for ellipsis */
    overflow: hidden;        /* needed for ellipsis */
    text-overflow: ellipsis;
    white-space: nowrap;     /* keep single line */
}

  ::v-deep .project-item {
    padding: 0 8px !important;
}

.table.b-table.table-hover > tbody > tr:hover > .table-b-table-default {
  background-image: unset!important;
  background-color: transparent !important;
}
.summary-indicator {
  padding: 0 4px;
  border-radius: 3px;
  line-height: 20px;
}

.summary-danger {
  @extend .summary-indicator;
  background-color: #FFDFC5;
}

.summary-success {
  @extend .summary-indicator;
  background-color: #C3E6CB;
}
.thead-row {
  display: none;
  &.sticky-head {
    min-height: 70px;
    padding: 0;
    display: table-row;
    align-items: center;
    background-color: #fff;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 9;

    .sticky-head__second-line {
      span {
        padding: 6px 5px 10px;

        &:first-child {
          padding: 6px 10px 10px 0;
        }
        &:last-child {
          padding: 6px 0px 10px 10px;
        }
      }
    }
  }
}
.invoice-badge {
  font-size: 14px;
  padding: 5px 12px;
  background-color: #DEEBFF;
}
.not-paid{
  background-color: #ede742;
}
.invoice-more {
  height: 100%;
  color: #007bff;
  cursor: pointer;
}
.invoice-td {
  display: flex;
  align-items: center;
  .b-toast {
    position: absolute;
  }
}
.variable-td {
  min-width: 100px;
  .val {
    font-weight: bold;
    text-decoration: underline;
  }

  input.form-control {
    width: 40px;
    height: 25px;
    padding: 4px;
    min-height: 25px;
    text-align: center;
  }
}
.material-icons {
  &.done {
    color: #3FA52F;
  }
  &.cancel {
    color: #DC3545;
  }
}
.multiselect-projects {
  width: 200px;
}
</style>
